'use client';
import React from 'react';
import Link from 'next/link';
import type { Language } from '@/lib/i18n';
import { t } from '@/lib/i18n';
import { Star, Trophy, ArrowRight, RotateCcw } from 'lucide-react';

interface Props {
  language: Language;
  xpEarned: number;
  accuracy: number;
  lessonTitle: string;
  onRestart: () => void;
  levelNumber?: number;
  allLessonsCompleted?: boolean;
}

export default function ResultStep({ language, xpEarned, accuracy, lessonTitle, onRestart, levelNumber, allLessonsCompleted }: Props) {
  const isExcellent = accuracy >= 90;
  const isGood = accuracy >= 70 && accuracy < 90;

  return (
    <div className="flex flex-col items-center text-center py-6 animate-bounce-in">
      {/* Trophy */}
      <div className="w-20 h-20 rounded-full gradient-primary flex items-center justify-center mb-5 xp-glow">
        <Trophy size={36} className="text-white" />
      </div>

      <h2 className="text-2xl font-700 text-foreground mb-2">
        {t(language, 'lesson_complete')}
      </h2>
      <p className="text-sm text-muted-foreground mb-6">{lessonTitle}</p>

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-4 w-full max-w-xs mb-6">
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Star size={16} className="text-primary" />
            <span className="text-2xl font-800 text-primary font-tabular">+{xpEarned}</span>
          </div>
          <p className="text-xs text-muted-foreground font-500">{t(language, 'lesson_xp_earned')}</p>
        </div>
        <div
          className={`border rounded-xl p-4 ${
            isExcellent
              ? 'bg-green-50 border-green-200'
              : isGood
              ? 'bg-amber-50 border-amber-200' :'bg-red-50 border-red-200'
          }`}
        >
          <p
            className={`text-2xl font-800 font-tabular mb-1 ${
              isExcellent ? 'text-success' : isGood ? 'text-accent' : 'text-danger'
            }`}
          >
            {accuracy}%
          </p>
          <p className="text-xs text-muted-foreground font-500">{t(language, 'lesson_accuracy')}</p>
        </div>
      </div>

      {/* Message */}
      <div className={`w-full max-w-xs p-4 rounded-xl mb-6 ${
        isExcellent ? 'bg-green-50 border border-green-200' : isGood ? 'bg-amber-50 border border-amber-200' : 'bg-red-50 border border-red-200'
      }`}>
        <p className={`text-sm font-600 ${isExcellent ? 'text-green-800' : isGood ? 'text-amber-800' : 'text-red-800'}`}>
          {isExcellent
            ? language === 'fr' ? '🌟 Excellent travail! Vous maîtrisez ce concept.' : '🌟 Отличная работа! Вы освоили эту концепцию.'
            : isGood
            ? language === 'fr' ? '👍 Bon travail! Continuez ainsi.' : '👍 Хорошая работа! Продолжайте в том же духе.'
            : language === 'fr' ? '💪 Continuez à pratiquer pour améliorer votre score.' : '💪 Продолжайте практиковаться для улучшения результата.'}
        </p>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-3 w-full max-w-xs">
        {allLessonsCompleted && levelNumber ? (
          <Link
            href={`/lesson-screen?levelTest=true&level=${levelNumber}`}
            className="flex items-center justify-center gap-2 w-full py-3 bg-amber-500 text-white font-600 rounded-xl text-sm hover:bg-amber-600 transition-all duration-150 active:scale-95"
          >
            <Trophy size={15} />
            {language === 'fr' ? `Passer le test du niveau ${levelNumber}` : `Пройти тест уровня ${levelNumber}`}
          </Link>
        ) : (
          <Link
            href="/learning-path-screen"
            className="flex items-center justify-center gap-2 w-full py-3 bg-primary text-white font-600 rounded-xl text-sm hover:bg-primary/90 transition-all duration-150 active:scale-95"
          >
            {t(language, 'lesson_next_lesson')}
            <ArrowRight size={15} />
          </Link>
        )}
        <Link
          href="/learning-path-screen"
          className="flex items-center justify-center gap-2 w-full py-3 border border-border text-muted-foreground font-600 rounded-xl text-sm hover:bg-secondary hover:text-foreground transition-all duration-150 active:scale-95"
        >
          {language === 'fr' ? 'Retour au parcours' : 'Вернуться к пути'}
        </Link>
        <button
          onClick={onRestart}
          className="flex items-center justify-center gap-2 w-full py-3 border border-border text-muted-foreground font-600 rounded-xl text-sm hover:bg-secondary hover:text-foreground transition-all duration-150 active:scale-95"
        >
          <RotateCcw size={14} />
          {language === 'fr' ? 'Recommencer la leçon' : 'Повторить урок'}
        </button>
      </div>
    </div>
  );
}